import { lazy, memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../../store';
import { Box, Button, List, Grid, Pagination, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from 'oidc-react';
import { useTranslation } from 'react-i18next';
import CreateConfigurationForm from './CreateConfigurationForm';
import { SelectComponent } from '../../InputComponents/SelectComponent';
import { ProductCardComponent } from './ProductCardComponent';
import {
  ETabValue,
  RecordsPerPage,
  EFilterRootOption,
  ECatalogType,
} from '../../../data/Constants';
import {
  getProductCatalog,
  getProductFilter,
  setProductCatalogDetails,
} from '../../../store/states/ProductCatalogSlice';
import { getUserPreferences } from '../../../store/states/UserPreferencesSlice';
import { changeLandingTab } from '../../../store/states/ConfigurationSlice';
import { getClaims } from '../../../store/states/ClaimsDataSlice';
import {
  filterIds,
  getAvailableCountries,
  getSelectedCodesWithAgCodes,
  isFilterDataAvailable,
  prepareTreeCodes,
} from '../../../store/Helpers/LandingHelper';
import { getUserDetails } from '../../../store/states/UserSlice';
import type {
  IProduct,
  IProductFilterOptions,
  ISimpleTreeProps,
  IProductFilter,
  IConfigDialogProp,
  IFilterOption,
} from '../../../../types';
import type { IProductCatalogRequest } from '../../../../types/IApiTypes';

const SimpleTreeViewComponent = lazy(() => import('../../SimpleTreeViewComponent'));

/**
 * This component perform the role to show the product portflio list, filter and pagination.
 * @returns {JSX.Element}
 */

const ProductPortfolio = () => {
  const initialFilterOptionValue = { channels: [], business: [] };
  const auth = useAuth();
  const token = auth?.userData?.access_token ?? '';
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  //selectors
  const productSettings = useSelector((state: AppState) => state.productCatalog);
  const configuration = useSelector((state: AppState) => state.configuration);

  const [productId, setProductId] = useState<string>('');
  const [productPackageType, setProductPackageType] = useState<string>(ECatalogType.Release);
  const [prodList, setProdList] = useState<IProduct[]>([]);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogProdDesc, setDialogProdDesc] = useState<string>('');
  const [countriesAvailable, setCountriesAvailable] = useState<IProduct['countries']>([]);
  const [catalogPage, setCatalogPage] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RecordsPerPage[0]); //Default records size is 15
  const [totalCatalogRecords, setTotalCatalogRecords] = useState<number>(1); //Default records size is 1
  const [filterOptions, setFilterOptions] =
    useState<IProductFilterOptions>(initialFilterOptionValue);
  const [availableFilterCodes, setAvailableFilterCodes] =
    useState<IProductFilter>(initialFilterOptionValue);
  const [filteredCodes, setFilteredCodes] = useState<IProductFilter>(initialFilterOptionValue);

  /**
   * Loads initial data: user details, preferences, claims, product catalog, and product filter.
   * @returns {void}
   */
  const loadInitialData = (): void => {
    const { page, limit } = productSettings.productCatalogDetails;
    dispatch(getUserDetails({ token: token }));
    dispatch(getUserPreferences({ token: token }));
    dispatch(getClaims({ token: token }));
    fetchProductCatalog({ page: page, limit: limit, searchKey: '' });
    fetchProductFilter();
  };

  /**
   * Fetches the product catalog based on page, limit, search value, and selected codes.
   * @param {Object} params - Parameters for fetching product catalog.
   * @param {number} params.page - Current page number.
   * @param {number} params.limit - Number of records per page.
   * @param {string} params.searchKey - Search value for products.
   * @param {IProductFilter} [params.selectedCodes] - Selected filter codes.
   * @returns {void}
   */
  const fetchProductCatalog = ({
    page,
    limit,
    searchKey,
    selectedCodes = filteredCodes,
  }: {
    page: number;
    limit: number;
    searchKey: string;
    selectedCodes?: IProductFilter;
  }): void => {
    const business = filterIds(selectedCodes.business, EFilterRootOption.Business);
    const channels = filterIds(selectedCodes.channels, EFilterRootOption.Channel);
    const params: IProductCatalogRequest = {
      token: token,
      page: page,
      limit: limit,
      searchKey: searchKey,
      agCodes: business,
      channelCodes: channels,
    };
    dispatch(getProductCatalog(params));
  };

  /**
   * Fetches the product filter options.
   * @returns {void}
   */
  const fetchProductFilter = (): void => {
    dispatch(getProductFilter({ token: token }));
  };

  /**
   * Handles change in records per page selection.
   * @param {React.ChangeEvent<Element>} event - Change event for records per page.
   * @returns {void}
   */
  const handleChangeForRPP = (event: React.ChangeEvent<Element>): void => {
    const target = event.target as HTMLSelectElement;
    const value = Number(target.value);
    setRecordsPerPage(value);
    //Adding the associated AgCodes to business filter beyond the AgCode selected.
    const selectedCodes = getSelectedCodesWithAgCodes(filterOptions, {
      ...filteredCodes,
    });
    fetchProductCatalog({
      page: 1,
      limit: value,
      searchKey: configuration.search.key ?? '',
      selectedCodes: selectedCodes,
    });
  };

  /**
   * Handles refresh action to reset filters and reload product catalog and filter.
   * @returns {void}
   */
  const handleRefresh = (): void => {
    setFilteredCodes(initialFilterOptionValue);
    dispatch(changeLandingTab({ value: true, tabValue: ETabValue.ProductPortfolio }));
    fetchProductCatalog({
      page: 1,
      limit: recordsPerPage,
      searchKey: '',
      selectedCodes: initialFilterOptionValue,
    });
    fetchProductFilter();
  };

  /**
   * Handles change in filter tree selection from the left pane.
   * @param {IFilterOption} selectedCodes - Selected codes from the filter section.
   * @returns {void}
   */
  const handleFilteredCodes = (selectedCodes: IFilterOption): void => {
    const { limit, totalRecords } = productSettings.productCatalogDetails;
    // Convert IFilterOption to IProductFilter
    const convertedCodes: IProductFilter = selectedCodes as unknown as IProductFilter;
    setFilteredCodes(convertedCodes);
    const selectedWithAgCodes: IProductFilter = getSelectedCodesWithAgCodes(filterOptions, {
      ...convertedCodes,
    });
    dispatch(
      setProductCatalogDetails({
        page: 1,
        limit,
        totalRecords: totalRecords ?? 1,
        agCodes: selectedWithAgCodes.business,
        channelCodes: selectedWithAgCodes.channels,
      })
    );
    fetchProductCatalog({
      page: 1,
      limit: limit,
      searchKey: configuration.search.key ?? '',
      selectedCodes: selectedWithAgCodes,
    });
  };

  /**
   * Handles pagination change event.
   * @param {React.ChangeEvent<unknown>} _event - Pagination change event.
   * @param {number} value - Current selected page.
   * @returns {void}
   */
  const handleChange = (_event: React.ChangeEvent<unknown>, value: number): void => {
    const { business, channels } = filteredCodes;
    const { totalRecords } = productSettings.productCatalogDetails;
    //Adding the associated AgCodes to business filter beyond the AgCode selected.
    const selectedCodes = getSelectedCodesWithAgCodes(filterOptions, {
      ...filteredCodes,
    } as IProductFilter);
    if (productSettings.productCatalog[value]) {
      setProdList(productSettings.productCatalog[value]);
    } else {
      fetchProductCatalog({
        page: value,
        limit: recordsPerPage,
        searchKey: configuration.search.key ?? '',
        selectedCodes: selectedCodes,
      });
    }
    setCatalogPage(value);
    dispatch(
      setProductCatalogDetails({
        page: value,
        limit: recordsPerPage,
        totalRecords: totalRecords,
        agCodes: business,
        channelCodes: channels,
      })
    );
  };

  /**
   * Opens the dialog to configure the product.
   * @param {{ productId: string, productDescription: string }} prod - Product info for dialog.
   * @returns {void}
   */
  const openDialog = (prod: {
    productId: string;
    productDescription: string;
    packageType: string;
  }): void => {
    setProductId(prod.productId);
    setDialogProdDesc(`${prod.productDescription}`);
    setProductPackageType(prod.packageType);
    setShowDialog(true);
  };

  /**
   * Closes the configuration dialog.
   * @returns {void}
   */
  const closeDialog = (): void => {
    setShowDialog(false);
  };

  /**
   * Loads initial data when token changes.
   */
  useEffect(() => {
    if (token) {
      loadInitialData();
    }
  }, [token]);

  /**
   * Updates state when product filter options or catalog change.
   */
  useEffect(() => {
    const { page, limit, totalRecords } = productSettings.productCatalogDetails;
    setCatalogPage(page);
    setRecordsPerPage(limit);
    setTotalCatalogRecords(Math.ceil(totalRecords / limit) || 1);
    setProdList(productSettings.productCatalog[page] || []);
    if (productSettings.productFilterOptions) {
      setFilterOptions(productSettings.productFilterOptions);
    }
    const filterCodes = prepareTreeCodes(productSettings.productFilterOptions);
    setAvailableFilterCodes(filterCodes);
  }, [productSettings.productFilterOptions, productSettings.productCatalog]);

  /**
   * Updates filtered country data when productId changes.
   */
  useEffect(() => {
    const countryData = getAvailableCountries(prodList, productId);
    setCountriesAvailable(countryData);
  }, [productId]);

  /**
   * The filterTreeProps is the schema for the creating filter tree on the product portfolio left pane
   */

  const filterTreeProps: ISimpleTreeProps = {
    treeNodes: [
      {
        type: EFilterRootOption.Channel.toLowerCase(),
        enumValue: EFilterRootOption.Channel,
        label: t('labels.channel'),
        expanded: EFilterRootOption.Channel,
        treeItems: filterOptions.channels,
      },
      {
        type: EFilterRootOption.Business.toLowerCase(),
        enumValue: EFilterRootOption.Business,
        label: t('labels.business'),
        expanded: EFilterRootOption.Channel,
        treeItems: filterOptions.business,
      },
    ],
    availableFilterCodes: availableFilterCodes,
    filteredCodes: filteredCodes,
    initialExpandValue: { channels: true, business: false },
    filterDataAvailable: isFilterDataAvailable(filterOptions),
  };

  /**
   * Returns the label to display when no products are found.
   * @returns {JSX.Element}
   */
  const getNoProductFoundLabel = (): React.ReactElement => {
    const hasFilteredCodes =
      filteredCodes.business?.length != 0 ||
      filteredCodes.channels?.length != 0 ||
      configuration.search.key;
    return (
      <span className="noData-label" data-testid="noProductLabel">
        {hasFilteredCodes ? t('labels.noDataFoundForCriteria') : t('labels.noDataFound')}
      </span>
    );
  };

  return (
    <Box className="product-portfolio-container">
      <Box className="hierarchy-container" style={{ marginTop: '25px' }}>
        {productSettings.loading ? (
          <div className="loader-placeholder" data-testid="loaderPlaceholder">
            {t('loaderMessage.default')} ...
          </div>
        ) : (
          <SimpleTreeViewComponent
            filterTreeProps={filterTreeProps}
            handleChanges={handleFilteredCodes}
          />
        )}
      </Box>

      {productSettings.productCatalog[productSettings.productCatalogDetails.page]?.length === 0 ? (
        getNoProductFoundLabel()
      ) : (
        <Box className="list-Section-Tab list-Section-Tab::-webkit-scrollbar">
          <Box className="recordsChange-conatiner">
            <SelectComponent
              name="recordsPerPage"
              cssStyle="recordsPerPage"
              value={recordsPerPage}
              options={RecordsPerPage}
              onChangeCallBack={handleChangeForRPP}
            />
            <Button
              className="refresh-button common-btn"
              color="primary"
              variant="text"
              startIcon={<RefreshIcon />}
              onClick={() => {
                handleRefresh();
              }}
            >
              {t('button.refresh')}
            </Button>
          </Box>
          <Box className="list-Section list-Section::-webkit-scrollbar">
            <List className="list-Items ">
              <Grid container spacing={1} className="list-ItemsContainer">
                {prodList.map((prod: IConfigDialogProp) => (
                  <ProductCardComponent key={`${prod.productId}-${prod.packageType}`} prod={prod} openDialog={openDialog} />
                ))}
              </Grid>
            </List>
          </Box>

          <Stack spacing={1} className="catalog-pagination">
            <Pagination
              color="primary"
              count={totalCatalogRecords}
              page={catalogPage}
              onChange={handleChange}
            />
          </Stack>
        </Box>
      )}
      {showDialog && (
        <CreateConfigurationForm
          countries={countriesAvailable}
          productId={productId}
          productDescription={dialogProdDesc}
          closeDialog={closeDialog}
          showDialog={showDialog}
          packageType={productPackageType}
        />
      )}
    </Box>
  );
};

export default memo(ProductPortfolio);
